import { makeVar, useReactiveVar } from '@apollo/client';
import { Sheet, useToast } from '@hover/blueprint';
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router';

import { CreatingProjectLoading } from 'src/modules/homeownerApp/components/UploadImage/CreatingProjectLoading';
import { ProjectConfirmation } from 'src/modules/homeownerApp/components/UploadImage/ProjectConfirmation';
import { SpaceTypeSheetContent } from 'src/modules/homeownerApp/components/UploadImage/SpaceTypeSheetContent';
import { useCtaText } from 'src/modules/homeownerApp/components/UploadImage/hooks';
import { designerAnonymousSessionVar } from 'src/shared/graphql/client/link/designerAnonymousSession';
import type { AiOriginalImage, Project } from 'src/shared/graphql/generated';
import { useAiSpaceTypesQuery } from 'src/shared/graphql/generated';
import {
  TranslationNamespace,
  useIsEmbed,
  useTimeout,
  useTranslate,
} from 'src/shared/hooks';
import { useGlobalStorageContext } from 'src/shared/hooks/useGlobalStorage';
import { Route } from 'src/shared/route';
import { EventType, emitPageAction } from 'src/shared/telemetry';

import homeownerAppTranslations from '../../../../../locales/en-US/homeownerApp.json'; // eslint-disable-line
import { useActiveOriginalImage, useHomeownerAppContext } from '../../hooks';
import { useUploadOriginalImage } from '../../hooks/useUploadOriginalImage';
import { HomeownerAppMenuNames } from '../../providers/HomeownerAppProvider';
import { OriginalImageStatus } from '../../static_values';
import { isSample } from '../../types';

const UPLOAD_IMAGE_STATUS_POLLING_INTERVAL = 1000;
const UPLOAD_IMAGE_STATUS_ERROR_TIMEOUT = 30000;

export const isUploadingOriginalImageVar = makeVar<boolean>(false);
export const closeUploadSheetAfterUpload = makeVar<boolean>(false);
export const addUploadToProject = makeVar<Project | undefined>(undefined);
export const uploadToProjectWithoutSelection = makeVar<boolean>(false);
export const alwaysCreateNewProject = makeVar<boolean>(false);

export type UploadImageSubmitProps = {
  overrideSpaceType?: string;
  projectId?: number;
};

const UploadImage = ({
  email,
  handleImageStatusDone,
  orgId,
  orgDisplayName,
  setImageFileToUpload,
  imageFileToUpload,
  fromLLP = false,
}: {
  email?: string;
  orgId?: number;
  orgDisplayName?: string;
  handleImageStatusDone: ({ image }: { image: AiOriginalImage }) => void;
  imageFileToUpload: File | undefined;
  setImageFileToUpload: (imageFile?: File | undefined) => void;
  fromLLP?: boolean;
}) => {
  const isEmbed = useIsEmbed();
  const consentMandatory = isEmbed;

  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);

  const history = useHistory();
  const errorToast = useToast({
    duration: 5000,
    isClosable: true,
  });

  // TODO: Refactor to use useReducer
  const [uploadedImageId, setUploadedImageId] = useState<number | undefined>();
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [spaceType, setSpaceType] = useState<string>('');
  const [spaceTypes, setSpaceTypes] = useState<string[]>([]);
  const [imageUrl, setImageUrl] = useState<string>('');
  const [agreedToTerms, setAgreedToTerms] = useState<boolean>(false);
  const [showProjectSelection, setShowProjectSelection] =
    useState<boolean>(false);
  const { loggedIn, trackingData, setCurrentMenu } = useHomeownerAppContext();
  const location = useLocation<{ preSelectedSpaceType?: string }>();
  const { setHomeownerAppSpaceType, setAffOrgPublicId } =
    useGlobalStorageContext();
  const searchParams = new URLSearchParams(location.search);
  const { preSelectedSpaceType } = location.state ?? {};
  const affOrgId = searchParams.get('aff_org_id');
  const ctaText = useCtaText();
  const projectToUploadTo = addUploadToProject();
  const uploadWithoutSelection = uploadToProjectWithoutSelection();
  const shouldShowProjectSelection =
    projectToUploadTo && !alwaysCreateNewProject() && !uploadWithoutSelection;

  const setUploading = useCallback((uploading: boolean) => {
    setIsUploading(uploading);
    isUploadingOriginalImageVar(uploading);
  }, []);

  useEffect(() => {
    if (!affOrgId) return;

    setAffOrgPublicId(affOrgId);
  }, [affOrgId, setAffOrgPublicId]);

  const designerAnonymousSession = useReactiveVar(designerAnonymousSessionVar);

  const { image, startPolling, stopPolling } = useActiveOriginalImage({
    originalImageId: uploadedImageId,
    options: { fetchPolicy: 'network-only' },
  });

  const { data: spaceTypesData } = useAiSpaceTypesQuery({
    variables: { orgId },
    // Does not get shown to user on load
    ssr: false,
  });

  useEffect(() => {
    setSpaceTypes(spaceTypesData?.aiSpaceTypes?.spaceTypes ?? []);
  }, [spaceTypesData]);

  useEffect(() => {
    if (uploadedImageId && !image?.status) {
      startPolling(UPLOAD_IMAGE_STATUS_POLLING_INTERVAL);
    }

    return stopPolling;
  }, [startPolling, uploadedImageId, stopPolling, image?.status]);

  const uploadImage = useUploadOriginalImage();

  useEffect(() => {
    const url = imageFileToUpload ? URL.createObjectURL(imageFileToUpload) : '';
    setImageUrl(url);
    return url ? () => URL.revokeObjectURL(url) : () => null;
  }, [imageFileToUpload]);

  const resetUploadForm = useCallback(() => {
    setUploading(false);
    setImageFileToUpload();
    setSpaceType('');
    setAgreedToTerms(false);
    setShowProjectSelection(false);
    setUploadedImageId(undefined);
  }, [setImageFileToUpload, setUploading]);

  const handleClose = () => {
    history.replace({
      pathname: history.location.pathname,
      state: {
        ...(history.location.state ?? {}),
        preSelectedSpaceType: undefined,
      },
    });
    closeUploadSheetAfterUpload(false);
    alwaysCreateNewProject(false);
    uploadToProjectWithoutSelection(false);
    resetUploadForm();
    setAffOrgPublicId();
  };

  const [serverTimeout, clearServerTimeout] = useTimeout();
  const missingConsent = !agreedToTerms && consentMandatory;

  const handleSubmit = async ({
    overrideSpaceType,
    projectId,
  }: UploadImageSubmitProps = {}) => {
    const spaceTypeToUse = overrideSpaceType ?? spaceType;
    setHomeownerAppSpaceType(spaceTypeToUse);

    // No action if no image is selected
    if (!imageFileToUpload) return;

    // check if user is logged in
    // all genai should be behind authentication:
    // cf. https://hoverinc.atlassian.net/wiki/spaces/EN/pages/3463479328/2024-04-17+Disallow+users+from+uploading+PII+unauthenticated
    // TODO: We are temporarily allowing anonymous sessions to experiment with
    //  third party integrations via an embedded experience. When the experiment
    //  is over, remove the allowance for designerAnonymousSession
    if (!loggedIn && !designerAnonymousSession && !fromLLP) {
      setCurrentMenu(HomeownerAppMenuNames.SIGN_IN);
      return;
    }

    setUploading(true);

    let marketingConsent;
    if (agreedToTerms) {
      marketingConsent = homeownerAppTranslations.uploadImage.embedLegalText;
    }

    try {
      const uploadedImage = await uploadImage({
        image: imageFileToUpload,
        email,
        spaceType: spaceTypeToUse,
        marketingConsent,
        projectId,
      });

      const newOriginalImageId = uploadedImage?.id;
      setUploadedImageId(newOriginalImageId);

      if (newOriginalImageId) {
        emitPageAction(EventType.DESIGNER_ORIGINAL_IMAGE_UPLOADED, {
          aiOriginalImageId: `${newOriginalImageId}`,
        });
      }
    } catch (e) {
      errorToast({
        status: 'error',
        description: t('uploadPhotoSheet.uploadErrorMessage'),
      });
      setUploading(false);
    }
  };

  const handleNext = () => {
    if (shouldShowProjectSelection) {
      setShowProjectSelection(true);
      return;
    }

    void handleSubmit();
  };

  useEffect(() => {
    const originalSpaceTypes = spaceTypesData?.aiSpaceTypes?.spaceTypes ?? [];
    const filteredSpaceTypes = preSelectedSpaceType
      ? originalSpaceTypes.filter(type => type !== 'exterior')
      : originalSpaceTypes;

    setSpaceTypes(filteredSpaceTypes);
  }, [preSelectedSpaceType, spaceTypesData?.aiSpaceTypes?.spaceTypes]);

  useEffect(() => {
    if (isUploading) {
      return;
    }

    if (
      imageFileToUpload &&
      preSelectedSpaceType === 'exterior' &&
      !missingConsent
    ) {
      // auto submit
      void handleSubmit({ overrideSpaceType: preSelectedSpaceType });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUploading, imageFileToUpload, preSelectedSpaceType]);

  useEffect(() => {
    if (uploadedImageId && !image?.status) {
      serverTimeout(() => {
        if (image?.status) return;

        stopPolling();
        resetUploadForm();
        history.replace({
          pathname: Route.HOMEOWNER_APP_HOME.path,
          state: {
            ...(history.location.state ?? {}),
            uploadErrorMessage: 'No status',
          },
        });
      }, UPLOAD_IMAGE_STATUS_ERROR_TIMEOUT);
    }

    return clearServerTimeout;
  }, [
    uploadedImageId,
    serverTimeout,
    clearServerTimeout,
    history,
    image?.status,
    stopPolling,
    resetUploadForm,
  ]);

  useEffect(() => {
    // AiOriginalImage status check
    if (!uploadedImageId || !isUploading) return;
    if (isSample(image as AiOriginalImage)) return;
    if (!image) return;

    if (image?.status !== null) {
      stopPolling();
      resetUploadForm();
    }

    if (image?.status === OriginalImageStatus.FAILED) {
      history.replace({
        pathname: Route.HOMEOWNER_APP_HOME.path,
        state: {
          ...(history.location.state ?? {}),
          uploadErrorMessage: image.statusDescription,
        },
      });
    } else if (image?.status === OriginalImageStatus.DONE) {
      handleImageStatusDone({ image: image as AiOriginalImage });
    }
  }, [
    isUploading,
    history,
    image,
    image?.status,
    stopPolling,
    uploadedImageId,
    handleImageStatusDone,
    spaceType,
    orgDisplayName,
    orgId,
    resetUploadForm,
  ]);

  if (!imageFileToUpload) return null;
  if ((preSelectedSpaceType || alwaysCreateNewProject()) && isUploading)
    return <CreatingProjectLoading imageUrl={imageUrl} />;

  return (
    <Sheet.Container
      autoFocus={false}
      blockScrollOnMount={!isEmbed}
      isOpen={!!imageUrl}
      onClose={handleClose}
      variant="bottom"
    >
      {showProjectSelection && spaceType ? (
        <ProjectConfirmation
          ctaText={ctaText}
          imageUrl={imageUrl}
          isLoading={isUploading}
          onNext={handleSubmit}
          project={projectToUploadTo!}
          spaceType={spaceType}
          trackingData={trackingData}
        />
      ) : (
        <SpaceTypeSheetContent
          agreedToTerms={agreedToTerms}
          canContinue={!spaceType || missingConsent}
          consentMandatory={consentMandatory}
          ctaText={ctaText}
          imageUrl={imageUrl}
          isLoading={isUploading}
          onNext={handleNext}
          setAgreedToTerms={setAgreedToTerms}
          setSpaceType={setSpaceType}
          spaceType={spaceType}
          spaceTypes={spaceTypes}
          trackingData={trackingData}
        />
      )}
    </Sheet.Container>
  );
};

export { UploadImage };
